import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, FORM_CONTROL_CLASS, FORM_LABEL_CLASS } from '@safetyhub/ui';
import {
  formatDateToLocal,
  isProcessSubjectKey,
  type LegalObligationRow,
  type WorkEnvironmentHazardView,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { describeLatestResult } from '../../lib/legalDocumentLabels';
import {
  addSiteMeasurementTarget,
  addSiteWorkType,
  endSiteMeasurementTarget,
  endSiteWorkType,
  getSiteMeasurementTargets,
  getSiteWorkTypes,
  legalSiteFactsQueryKeys,
  setSiteWorkTypesReviewed,
} from '../../lib/legalSiteFacts';
import { useToastStore } from '../../stores/toast';
import ResultRecordModal, { describeResultRecorded } from './ResultRecordModal';

/** 작업환경측정처럼 «공정 × 유해인자 대상» 의무인가 — 서버가 실어 준 대상 키·안내로만 가른다(의무 key를 하드코딩하지 않는다) */
export function isMeasurementTargetRow(row: LegalObligationRow): boolean {
  return row.subjectNotice === 'MEASUREMENT_TARGET_REQUIRED' || row.subjects.some((s) => isProcessSubjectKey(s.subjectKey));
}

export interface SiteLegalFactsPanelProps {
  companyId: string;
  siteId: string;
  canManage: boolean;
  /** 이 현장의 의무 목록(측정 대상 의무·결과 코드를 고르는 재료) */
  obligations: LegalObligationRow[];
  /** 현장 유효 시간대(서버 `LegalObligationsResponse.timezone`) — 측정일 변환과 「오늘」(2026-11-03 P2) */
  timezone: string;
  /** 명부·결과가 바뀌면 의무 목록을 다시 읽는다(판정·대상 하위 줄이 바뀐다) */
  onChanged: () => void | Promise<void>;
}

/**
 * 법정 서류의 **현장 사실 명부**(②-c 2부) — 의무 목록 아래에 두 묶음을 둔다:
 * - **특별교육 대상 작업**(E6 · 시행규칙 [별표 5] 제1호 라목 39종) — 명부 + 「검토 완료」. 검토 완료 전에는 명부가 비어도 특별교육은
 *   «해당 여부 확인 필요»이고, 검토 완료 + 0건이면 «해당 없음», 하나라도 있으면 «해당»이다(서버 `judgeWorkPerformed`).
 * - **작업환경측정 대상(공정 × 유해인자)**(E7 ⓒ · [별표 21]) — 쌍마다 작업환경측정의 대상 한 줄이 서고 **그 쌍의 최신 결과**가 다음 주기를
 *   정한다(초과 3개월 · 2회 연속 미만 1년 · 그 밖 반기). 결과 기록도 여기서 한다(쌍 하나를 골라).
 */
export default function SiteLegalFactsPanel({
  companyId,
  siteId,
  canManage,
  obligations,
  timezone,
  onChanged,
}: SiteLegalFactsPanelProps) {
  const measurementRow = obligations.find(isMeasurementTargetRow) ?? null;
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="site-legal-facts">
      <WorkTypesSection companyId={companyId} siteId={siteId} canManage={canManage} onChanged={onChanged} />
      <MeasurementTargetsSection
        companyId={companyId}
        siteId={siteId}
        canManage={canManage}
        measurementRow={measurementRow}
        timezone={timezone}
        onChanged={onChanged}
      />
    </div>
  );
}

// ── E6 특별교육 대상 작업 ───────────────────────────────────────────────────────────────

function WorkTypesSection({
  companyId,
  siteId,
  canManage,
  onChanged,
}: {
  companyId: string;
  siteId: string;
  canManage: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const queryKey = legalSiteFactsQueryKeys.workTypes(companyId, siteId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getSiteWorkTypes(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });
  const [code, setCode] = useState('');
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await onChanged();
  };
  const onError = (fallback: string) => (err: unknown) => showToast(apiErrorMessage(err) ?? fallback, 'error');
  const addMutation = useMutation({
    mutationFn: (c: number) => addSiteWorkType(companyId, siteId, c),
    onSuccess: async () => {
      setCode('');
      await refresh();
    },
    onError: onError('작업을 추가하지 못했습니다.'),
  });
  const endMutation = useMutation({
    mutationFn: (entryId: string) => endSiteWorkType(companyId, siteId, entryId),
    onSuccess: refresh,
    onError: onError('작업을 명부에서 빼지 못했습니다.'),
  });
  const reviewMutation = useMutation({
    mutationFn: (reviewed: boolean) => setSiteWorkTypesReviewed(companyId, siteId, reviewed),
    onSuccess: refresh,
    onError: onError('검토 완료를 저장하지 못했습니다.'),
  });

  const entries = data?.entries ?? [];
  const usedCodes = new Set(entries.map((e) => e.code));
  const options = (data?.catalog ?? []).filter((c) => !usedCodes.has(c.code));
  const busy = addMutation.isPending || endMutation.isPending || reviewMutation.isPending;

  return (
    <Card padding="md" radius="lg" elevation="sm">
      <h2 className="text-base font-bold text-ink-900">특별교육 대상 작업</h2>
      <p className="mt-1 text-xs text-ink-500">
        산업안전보건법 시행규칙 [별표 5] 제1호 라목(39종) 중 이 현장에서 하는 작업입니다. 명부를 확인했으면 「검토 완료」를 눌러야
        «해당 없음»이 판정됩니다 — 그 전에는 특별교육이 «해당 여부 확인 필요»로 남습니다.
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-ink-500">불러오는 중…</p>
      ) : error ? (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          {apiErrorMessage(error) ?? '작업 명부를 불러오지 못했습니다.'}
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="work-types-review">
            {data?.reviewedAt ? (
              <Badge tone="emerald" size="sm" dot>
                검토 완료 · {formatDateToLocal(new Date(data.reviewedAt))}
              </Badge>
            ) : (
              <Badge tone="amber" size="sm" dot>
                미검토
              </Badge>
            )}
            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => reviewMutation.mutate(!data?.reviewedAt)}
              >
                {data?.reviewedAt ? '검토 완료 취소' : '검토 완료'}
              </Button>
            )}
          </div>
          {entries.length === 0 ? (
            <p className="mt-3 text-sm text-ink-500">등록한 작업이 없습니다.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5" aria-label="특별교육 대상 작업 명부">
              {entries.map((e) => (
                <li key={e.id} className="flex items-start justify-between gap-2 text-sm">
                  <span className="text-ink-800">
                    <span className="font-semibold">{e.code}.</span> {e.name}
                  </span>
                  {canManage && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => endMutation.mutate(e.id)}>
                      빼기
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canManage && options.length > 0 && (
            <div className="mt-3 flex gap-2">
              <label htmlFor="legal-work-type-add" className="sr-only">
                추가할 작업
              </label>
              <select
                id="legal-work-type-add"
                className={FORM_CONTROL_CLASS}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
              >
                <option value="">작업 고르기…</option>
                {options.map((c) => (
                  <option key={c.code} value={String(c.code)}>
                    {c.code}. {c.name}
                  </option>
                ))}
              </select>
              <Button size="sm" disabled={!code || busy} onClick={() => addMutation.mutate(Number(code))}>
                추가
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── E7 ⓒ 작업환경측정 대상(공정 × 유해인자) + 결과 기록 ─────────────────────────────────

function MeasurementTargetsSection({
  companyId,
  siteId,
  canManage,
  measurementRow,
  timezone,
  onChanged,
}: {
  companyId: string;
  siteId: string;
  canManage: boolean;
  measurementRow: LegalObligationRow | null;
  timezone: string;
  onChanged: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const queryKey = legalSiteFactsQueryKeys.measurementTargets(companyId, siteId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getSiteMeasurementTargets(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });
  const [processName, setProcessName] = useState('');
  const [hazardCode, setHazardCode] = useState('');
  const [resultTarget, setResultTarget] = useState<{ subjectKey: string; label: string } | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await onChanged();
  };
  const addMutation = useMutation({
    mutationFn: () => addSiteMeasurementTarget(companyId, siteId, { processName, hazardCode }),
    onSuccess: async (r) => {
      setHazardCode('');
      showToast(r.created ? '측정 대상을 등록했습니다.' : '이미 등록된 측정 대상입니다.', 'success');
      await refresh();
    },
    onError: (err) => showToast(apiErrorMessage(err) ?? '측정 대상을 등록하지 못했습니다.', 'error'),
  });
  const endMutation = useMutation({
    mutationFn: (targetId: string) => endSiteMeasurementTarget(companyId, siteId, targetId),
    onSuccess: refresh,
    onError: (err) => showToast(apiErrorMessage(err) ?? '측정 대상을 빼지 못했습니다.', 'error'),
  });

  const hazards = data?.hazards ?? [];
  const hazardGroups = groupHazards(hazards);
  const processes = data?.processes ?? [];
  const busy = addMutation.isPending || endMutation.isPending;
  const subjectOf = (subjectKey: string) => measurementRow?.subjects.find((s) => s.subjectKey === subjectKey) ?? null;

  return (
    <Card padding="md" radius="lg" elevation="sm">
      <h2 className="text-base font-bold text-ink-900">작업환경측정 대상(공정 × 유해인자)</h2>
      <p className="mt-1 text-xs text-ink-500">
        시행규칙 [별표 21]의 유해인자를 공정(단위작업장소)별로 등록합니다. 쌍마다 작업환경측정 항목이 따로 서고, 그 쌍의 최신 측정 결과가
        다음 주기를 정합니다(노출기준 초과 3개월 · 2회 연속 미만·변화 없음 1년 · 그 밖 반기).
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-ink-500">불러오는 중…</p>
      ) : error ? (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          {apiErrorMessage(error) ?? '측정 대상을 불러오지 못했습니다.'}
        </p>
      ) : (
        <>
          {processes.length === 0 ? (
            <p className="mt-3 text-sm text-ink-500">등록한 측정 대상이 없습니다.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-3" aria-label="작업환경측정 대상">
              {processes.map((p) => (
                <li key={p.id}>
                  <p className="text-sm font-semibold text-ink-900">{p.name}</p>
                  <ul className="mt-1 flex flex-col gap-1">
                    {p.targets.map((t) => {
                      const subject = subjectOf(t.subjectKey);
                      const described =
                        subject?.latestResult && measurementRow
                          ? describeLatestResult(subject.latestResult, measurementRow.intervalVariants)
                          : null;
                      return (
                        <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-subject-key={t.subjectKey}>
                          <span className="text-ink-800">{t.hazardName}</span>
                          <span className="flex items-center gap-1.5">
                            {/* ②-c E7 — 기록한 측정 결과 · 그 결과표가 미등록이면 「결과표를 등록하면 완료」(서버 판정 · 의무 목록 하위 줄과 같은 함수) */}
                            {described && (
                              <span
                                className={`text-xs ${described.pendingEvidence ? 'text-amber-700' : 'text-ink-400'}`}
                                data-testid="pair-latest-result"
                                data-pending-evidence={described.pendingEvidence ? 'true' : 'false'}
                              >
                                {described.text}
                              </span>
                            )}
                            {subject?.currentPeriod.kind === 'period' && (
                              <span className="text-xs text-ink-500">기한 {formatDateToLocal(new Date(subject.currentPeriod.deadline))}</span>
                            )}
                            {canManage && measurementRow && (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => setResultTarget({ subjectKey: t.subjectKey, label: `${p.name} · ${t.hazardName}` })}
                              >
                                결과 기록
                              </Button>
                            )}
                            {canManage && (
                              <Button variant="ghost" size="sm" disabled={busy} onClick={() => endMutation.mutate(t.id)}>
                                빼기
                              </Button>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {canManage && (
            <div className="mt-4 grid gap-2">
              <div>
                <label htmlFor="legal-measure-process" className={FORM_LABEL_CLASS}>
                  공정(단위작업장소)
                </label>
                <input
                  id="legal-measure-process"
                  className={FORM_CONTROL_CLASS}
                  value={processName}
                  maxLength={100}
                  list="legal-measure-process-names"
                  onChange={(e) => setProcessName(e.target.value)}
                  placeholder="예: 도장 라인"
                  disabled={busy}
                />
                <datalist id="legal-measure-process-names">
                  {processes.map((p) => (
                    <option key={p.id} value={p.name} />
                  ))}
                </datalist>
              </div>
              <div>
                <label htmlFor="legal-measure-hazard" className={FORM_LABEL_CLASS}>
                  유해인자
                </label>
                <select
                  id="legal-measure-hazard"
                  className={FORM_CONTROL_CLASS}
                  value={hazardCode}
                  onChange={(e) => setHazardCode(e.target.value)}
                  disabled={busy}
                >
                  <option value="">유해인자 고르기…</option>
                  {hazardGroups.map(([category, items]) => (
                    <optgroup key={category} label={category}>
                      {items.map((h) => (
                        <option key={h.code} value={h.code}>
                          {h.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <Button size="sm" disabled={!processName.trim() || !hazardCode || busy} onClick={() => addMutation.mutate()}>
                  측정 대상 등록
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {resultTarget && measurementRow && (
        <ResultRecordModal
          companyId={companyId}
          siteId={siteId}
          row={measurementRow}
          subjectKey={resultTarget.subjectKey}
          subjectLabel={resultTarget.label}
          timezone={timezone}
          onClose={() => setResultTarget(null)}
          onRecorded={async (result) => {
            setResultTarget(null);
            // 문구(소급 경고 · 개선 보고 사건)의 정본은 `describeResultRecorded` — 의무 목록의 결과 입구와 한 벌
            const { message, tone } = describeResultRecorded(result);
            showToast(message, tone);
            await onChanged();
          }}
        />
      )}
    </Card>
  );
}

/** 분류 순서를 지키며 묶는다(서버가 별표 순 `sortOrder`로 준다) */
function groupHazards(hazards: readonly WorkEnvironmentHazardView[]): Array<[string, WorkEnvironmentHazardView[]]> {
  const groups: Array<[string, WorkEnvironmentHazardView[]]> = [];
  for (const h of hazards) {
    const last = groups[groups.length - 1];
    if (last && last[0] === h.category) last[1].push(h);
    else groups.push([h.category, [h]]);
  }
  return groups;
}
// 결과 기록 모달은 `./ResultRecordModal`(의무 목록의 현장 단위 결과 입구와 한 벌 — ②-c1b)
